import { useState, useEffect, useMemo, useCallback } from "react";
import { Calendar, Search, Hospital, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import api from "../../api";
import Menu from "../../components/Menu.js";
import "./css/PosConsultPageHome.css";

import PatientCard from "./components/PatientCard.js";
import PatientPartialCard from "./components/PatientPartialCard.js";
import ProceduresModal from "./components/ProceduresModal.js";
import ScheduledProceduresModal from "./components/ScheduledProceduresModal.js";
import BudgetProceduresModal from "./components/BudgetProceduresModal.js";
import DoctorSelectionPage from "./components/DoctorSelectionPage.js";
import SchedulingPage from "./components/SchedulingPage.js";
import ConfirmationPage from "./components/ConfirmationPage.js";
import PaymentModal from "./components/PaymentModal.js";
import LoadingSpinner from "./components/LoadingSpinner.js";

import useFetchDataPosConsult from "./hooks/useFetchDataPosConsult.js";
import { usePusherChannel } from "../../hooks/usePusherChannel.js";
import { useStateContext } from "../../contexts/ContextProvider.js";

const normalizePatientData = (raw) => {
  const base = {
    id: raw.id,
    name: raw.name,
    cpf: raw.cpf,
    ...raw,
  };

  if (raw.patient) {
    return {
      ...base,
      id: raw.patient.id,
      name: raw.patient.name,
      cpf: raw.patient.cpf,
      procedures: raw.pending || [],
    };
  }

  return { ...base, procedures: [] };
};

const groupProceduresByDate = (procedures) => {
  const grouped = {};
  procedures.forEach((proc) => {
    const date = proc.created_at?.split("T")[0] ?? "sem-data";
    if (!grouped[date]) {
      grouped[date] = { cancelled: [], confirmed: [], scheduled: [] };
    }
    if (proc.scheduled) grouped[date].scheduled.push(proc);
    else if (proc.confirmed) grouped[date].confirmed.push(proc);
    else grouped[date].cancelled.push(proc);
  });
  return grouped;
};

function PosConsultPageHome() {
  const navigate = useNavigate();
  const { user } = useStateContext();

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState("main"); // main, doctorSelection, scheduling, confirmation
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [schedulingDetails, setSchedulingDetails] = useState({
    date: "",
    time: "",
    comments: "",
  });

  const options = useMemo(() => ({ date }), [date]);

  const {
    data: patientsAll,
    refetch: refetchPatientsAll,
  } = useFetchDataPosConsult(
    "/api/clinic-procedures/patient-summary-all-day-new",
    options
  );

  // Pusher Listeners
  const handlePusherUpdate = useCallback(async () => {
    await refetchPatientsAll();
  }, [refetchPatientsAll]);

  usePusherChannel('update-pos-doctor-attendance-sale-channel', 'update-pos-doctor-attendance-sale-event', handlePusherUpdate);
  usePusherChannel('update-pos-pharmacy-sale-channel', 'update-pos-pharmacy-sale-event', handlePusherUpdate);
  usePusherChannel('update-pharmacy-sale-channel', 'update-pharmacy-sale-event', handlePusherUpdate);
  usePusherChannel('update-pos-doctor-pharmacy-sale-channel', 'update-pos-doctor-pharmacy-sale-event', handlePusherUpdate);

  const handleLoadPatients = async () => {
    setLoadingPatients(true);
    try {
      await refetchPatientsAll();
    } catch (error) {
      console.error("Erro ao carregar pacientes:", error);
    } finally {
      setLoadingPatients(false);
    }
  };

  useEffect(() => {
    handleLoadPatients();
  }, [date]);

  useEffect(() => {
    const handleUpdateDate = () => {
      const today = new Date().toISOString().split("T")[0];
      if (date !== today) {
        setDate(today);
      }
    };

    window.addEventListener('updateDateAndRefetch', handleUpdateDate);

    return () => {
      window.removeEventListener('updateDateAndRefetch', handleUpdateDate);
    };
  }, [date]);

  window.addEventListener('refetchData', refetchPatientsAll);

  const normalizedPatients = useMemo(() => patientsAll?.data?.map(normalizePatientData) || [], [patientsAll]);

  const filteredPatients = useMemo(() => {
    if (!normalizedPatients.length) return [];
    const lowerSearch = searchTerm.toLowerCase();
    return normalizedPatients.filter((entry) => 
      (entry?.name?.toLowerCase() || "").includes(lowerSearch) || 
      (entry?.cpf || "").includes(searchTerm)
    );
  }, [normalizedPatients, searchTerm]);

  const handleOpenModal = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
    setCurrentPage("main");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowScheduledModal(false);
    setShowBudgetModal(false);
    setCurrentPage("main");
    setSelectedProcedures([]);
    setSelectedDoctor(null);
    setSchedulingDetails({
      date: "",
      time: "",
      comments: "",
    });
    setSelectedPatient(null);
  };

  const handleOpenBudgetModal = (patient) => {
    setSelectedPatient(patient);
    setShowBudgetModal(true);
  }

  const handleOpenScheduledModal = async (patient) => {
    try {
      const response = await api.post("/api/schedulings/getByCpf", {
        cpf: patient.cpf,
        include_trashed: true,
      });

      const schedules = response.data.data || [];
      console.log("Agendamentos: ", schedules);

      setSelectedPatient({
        ...patient,
        schedules: schedules,
        procedures_by_date: groupProceduresByDate(schedules),
      });

      setShowScheduledModal(true);
    } catch (error) {
      console.error("Erro ao buscar agendamentos por CPF:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível buscar os agendamentos.",
      });
    }
  };

  const getSellingPrice = (proc, doctor) => {
    const byId =
      doctor?.procedures?.find((p) => p.id === proc.procedure_id)
        ?.selling_price ??
      doctor?.procedures?.find((p) => p.id === proc.id)?.selling_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.value ?? proc.cost_price ?? proc.price ?? 0);
  };

  const getProcCostPrice = (proc, doctor) => {
    const byId =
      doctor?.procedures?.find((p) => p.id === proc.procedure_id)?.cost_price ??
      doctor?.procedures?.find((p) => p.id === proc.id)?.cost_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.cost_price ?? 0);
  };

  const handleConfirmScheduling = async () => {
      if (
        !schedulingDetails.date ||
        !schedulingDetails.time ||
        !schedulingDetails.comments
      ) {
        Swal.fire({
          title: "Erro",
          text: "Por favor, preencha todos os campos antes de confirmar.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const payload = {
        affiliate_company_id: selectedDoctor?.id || 0, // fallback padrão
        clinic_id:
          selectedDoctor?.clinic_id || selectedProcedures?.[0]?.clinic_id || 0,
        status: "0", // status inicial (ex: pendente)
        customer_cpf: selectedPatient?.cpf,
        customer_birth: selectedPatient?.patient?.birth || "",
        professional_name: selectedDoctor?.name,
        comment: schedulingDetails.comments,
        scheduling_date: `${schedulingDetails.date} ${schedulingDetails.time}:00`,
        executing_company_id: selectedDoctor.id || 0,
        procedures: selectedProcedures.map((procedure) => ({
          user_id: user.user.id,
          procedure_id: procedure.procedure_id || procedure.id,
          cbhpm_code: procedure.cbhpm_code || null,
          clinic_procedure_id: procedure.id,
          clinic_id: procedure.clinic_id,
          scheduling_id_secondary: procedure.scheduling_id_secondary,
          quantity: 1,
          agreement:
            procedure.payment?.type === "insurance"
              ? procedure.payment?.insurance
              : null,
          cost_price: getProcCostPrice(procedure, selectedDoctor),
          selling_price: getSellingPrice(procedure, selectedDoctor),
          requesting_company: procedure.professional_name || "",
          executing_company: selectedDoctor.name || "",
          insurance: procedure.payment?.insurance || null,
          date_scheduling: schedulingDetails.date,
          time_booked: `${schedulingDetails.time}:00`,
          price: getSellingPrice(procedure, selectedDoctor),
        })),
      };

      Swal.fire({
        title: "Aguarde",
        text: "Confirmando agendamento...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      })

      try {
        await api.post("/api/schedulings", payload);

        Swal.close();

        Swal.fire({
          title: "Sucesso",
          text: "Agendamento confirmado com sucesso!",
          icon: "success",
          confirmButtonText: "OK",
        });
        await refetchPatientsAll();
      } catch (error) {
        console.error("Erro ao confirmar agendamento", error);

        Swal.fire({
          title: "Erro",
          text: "Ocorreu um erro ao confirmar o agendamento. Tente novamente.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
      setCurrentPage("main");
      setSelectedProcedures([]);
      setSelectedDoctor(null);
      setSchedulingDetails({
        date: "",
        time: "",
        comments: "",
      });
  };

  const handleReschedule = async (payload) => {
    try {
      await api.post(
        "/api/schedulings/reschedule-items",
        payload
      );

      Swal.fire({
        title: "Sucesso",
        text: "Procedimentos reagendados com sucesso!",
        icon: "success",
      });

      await refetchPatientsAll();
      handleCloseModal();

    } catch (error) {
      console.error("❌ Erro ao reagendar procedimentos:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível reagendar os procedimentos.",
        icon: "error",
      });
    }
  };

  const handleSavePayment = async (formData) => {
    console.log('form data', formData);
    
    try {
      Swal.fire({
        title: "Aguarde",
        text: "Confirmando pagamento...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      })

      await api.post("/api/cashiers", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Accept": "application/json",
        },
      });
      await refetchPatientsAll();
      Swal.close();
      Swal.fire("Sucesso", "Pagamento registrado com sucesso.", "success");
    } catch (err) {
      console.error(err);
      Swal.close();
      Swal.fire("Erro", "Falha ao registrar pagamento.", "error");
    }
  };

  const handleRemoveProcedures = async (schedules, schedulesIds, requisition_id_foreign) => {
    if (!Array.isArray(schedules) || schedules.length === 0) return;

    try {
      await api.post(`/api/schedulings/remove-items`, {
        items: schedules.map((s) => ({
          scheduling_id: schedulesIds[0],
          procedure_id: s.procedure_id,
          requisition_id_foreign: s.requisition_id_foreign,
        })),
      });
      
      await refetchPatientsAll();
      Swal.fire("Sucesso", "Procedimento cancelado com sucesso!", "success");
      handleCloseModal();
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.message || "Erro inesperado.", "error");
    }
  };

  const handleMakePayment = async (patient) => {
    try {
      const response = await api.post("/api/schedulings/getByCpf", {
        cpf: patient.cpf,
        include_trashed: true,
      });

      const schedules = response.data.data || [];

      if (!schedules.length) {
        Swal.fire({
          title: "Erro",
          text: "Não há agendamentos encontrados para este paciente.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      setSelectedPatient({ ...patient, schedules });
      setSelectedSchedule({ schedules });
      setShowPaymentModal(true);
    } catch (error) {
      console.error("❌ Erro ao buscar agendamentos para pagamento:", error);
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar agendamentos para pagamento.",
        icon: "error",
      });
    }
  };

  const renderMainPage = () => (
    <>
      <header className="app-header">
        <div className="logo-container">
          <Hospital className="logo-icon" />
          <h1>Pós-Consultório</h1>
        </div>
        <div className="header-controls">
          <div className="date-picker">
            <Calendar className="date-icon" />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
      </header>

      <div className="search-container">
        <Search className="search-icon" />
        <input
          type="text"
          placeholder="Buscar Paciente por Nome ou CPF"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="patients-list">
        {loadingPatients ? (
          <LoadingSpinner message="Carregando pacientes..." />
        ) : (
          filteredPatients.map((patient) => (
            (patient.patient?.birth && patient.patient?.cpf) ? (
              <PatientCard
                key={patient.id}
                patient={patient}
                onViewDetails={() => setSelectedPatient(patient)}
                onViewProcedures={() => handleOpenModal(patient)}
                onViewScheduledProcedures={() => handleOpenScheduledModal(patient)}
                onMakePayment={() => handleMakePayment(patient)}
                onViewBudget={() => handleOpenBudgetModal(patient)}
              />
            ) : (
              <PatientPartialCard
                key={`partial-${patient.id}`}
                patient={patient}
              />
            )
          ))
        )}

        {!loadingPatients && filteredPatients.length === 0 && (
          <div className="no-results">
            <FileText size={48} />
            <p>Nenhum paciente encontrado</p>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <button className="button-cashier" onClick={() => navigate("/pos-consultorio/caixa")}>
          Caixa
        </button>
      </div>
    </>
  );

  const screenTela = (
    <div>
      <div className="app-container">
        {currentPage === "main" && renderMainPage()}

        {currentPage === "doctorSelection" && selectedPatient && (
          <DoctorSelectionPage
            patient={selectedPatient}
            procedures={selectedProcedures}
            onSelectDoctor={(doctor) => { setSelectedDoctor(doctor); setCurrentPage("scheduling"); }}
            onBack={() => setCurrentPage("main")}
          />
        )}

        {currentPage === "scheduling" && selectedPatient && selectedDoctor && (
          <SchedulingPage
            patient={selectedPatient}
            procedures={selectedProcedures}
            doctor={selectedDoctor}
            onSubmit={(details) => { setSchedulingDetails(details); setCurrentPage("confirmation"); }}
            onBack={() => setCurrentPage("doctorSelection")}
          />
        )}

        {currentPage === "confirmation" && selectedPatient && selectedDoctor && (
          <ConfirmationPage
            patient={selectedPatient}
            procedures={selectedProcedures}
            doctor={selectedDoctor}
            details={schedulingDetails}
            onConfirm={handleConfirmScheduling}
            onBack={() => setCurrentPage("scheduling")}
          />
        )}

        {/* Modals */}
        {showModal && selectedPatient && (
          <ProceduresModal
            patient={selectedPatient}
            onClose={handleCloseModal}
            onProceed={(procedures) => { setSelectedProcedures(procedures); setCurrentPage("doctorSelection"); setShowModal(false); }}
          />
        )}

        {showScheduledModal && selectedPatient && (
          <ScheduledProceduresModal
            patient={selectedPatient}
            onClose={handleCloseModal}
            onRemove={handleRemoveProcedures}
            onReschedule={handleReschedule}
          />
        )}

        {showBudgetModal && selectedPatient && (
          <BudgetProceduresModal
            open={showBudgetModal}
            patient={selectedPatient}
            onClose={handleCloseModal}
            onRefresh={refetchPatientsAll}
          />
        )}

        {showPaymentModal && selectedPatient && selectedSchedule && (
          <PaymentModal
            schedule={selectedSchedule}
            patient={selectedPatient}
            onSavePayment={handleSavePayment}
            onClose={() => setShowPaymentModal(false)}
          />
        )}
      </div>
    </div>
  );

  return <Menu screen={screenTela} />;
}

export default PosConsultPageHome;
